'use client';

import { useAuth } from '@clerk/clerk-react';
import { useMemo } from 'react';

/**
 * Permission keys - MUST match Clerk Dashboard + backend exactly!
 * Format: org:<feature>:<action>
 * Actions: view | manage
 */
export const PERMISSIONS = {
    // Orders
    ORDERS_VIEW: 'org:orders:view',
    ORDERS_MANAGE: 'org:orders:manage',

    // Payments
    PAYMENTS_VIEW: 'org:payments:view',
    PAYMENTS_MANAGE: 'org:payments:manage',

    // Customers
    CUSTOMERS_VIEW: 'org:customers:view',
    CUSTOMERS_MANAGE: 'org:customers:manage',

    // Subscriptions
    SUBSCRIPTIONS_VIEW: 'org:subscriptions:view',
    SUBSCRIPTIONS_MANAGE: 'org:subscriptions:manage',

    // Stores
    STORES_VIEW: 'org:stores:view',
    STORES_MANAGE: 'org:stores:manage',

    // Processors
    PROCESSORS_VIEW: 'org:processors:view',
    PROCESSORS_MANAGE: 'org:processors:manage',

    // Analytics (view only)
    ANALYTICS_VIEW: 'org:analytics:view',

    // Billing (view only)
    BILLING_VIEW: 'org:billing:view',

    // Team/Members
    TEAM_VIEW: 'org:team:view',
    TEAM_MANAGE: 'org:team:manage',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

/**
 * Role definitions for the UI
 */
export const ROLES = {
    ADMIN: 'org:admin',
    MEMBER: 'org:member',
} as const;

export type RoleKey = (typeof ROLES)[keyof typeof ROLES];

export const ROLE_INFO = {
    [ROLES.ADMIN]: {
        name: 'Admin',
        description: 'Full access to everything',
    },
    [ROLES.MEMBER]: {
        name: 'Member',
        description: 'Manage orders, payments, subscriptions only',
    },
} as const;

/**
 * Hook for checking user permissions
 *
 * @example
 * function OrdersPage() {
 *     const { can } = usePerm();
 *
 *     if (!can(PERMISSIONS.ORDERS_VIEW)) {
 *         return <AccessDenied />;
 *     }
 *
 *     return (
 *         <div>
 *             <OrdersList />
 *             {can(PERMISSIONS.ORDERS_MANAGE) && <RefundButton />}
 *         </div>
 *     );
 * }
 */
export function usePerm() {
    const { has, isLoaded, isSignedIn, orgRole } = useAuth();

    /**
     * Check if user has a specific permission
     */
    const can = useMemo(() => {
        return (permission: PermissionKey): boolean => {
            if (!isLoaded || !isSignedIn || !has) return false;
            return has({ permission });
        };
    }, [has, isLoaded, isSignedIn]);

    /**
     * Check if user has ANY of the specified permissions
     */
    const canAny = useMemo(() => {
        return (permissions: PermissionKey[]): boolean => {
            if (!isLoaded || !isSignedIn || !has) return false;
            return permissions.some((p) => has({ permission: p }));
        };
    }, [has, isLoaded, isSignedIn]);

    /**
     * Check if user has ALL of the specified permissions
     */
    const canAll = useMemo(() => {
        return (permissions: PermissionKey[]): boolean => {
            if (!isLoaded || !isSignedIn || !has) return false;
            return permissions.every((p) => has({ permission: p }));
        };
    }, [has, isLoaded, isSignedIn]);

    /**
     * Check if user has a specific role
     */
    const hasRole = useMemo(() => {
        return (role: RoleKey): boolean => {
            if (!isLoaded || !isSignedIn) return false;
            return orgRole === role;
        };
    }, [isLoaded, isSignedIn, orgRole]);

    /**
     * Check if user is admin
     */
    const isAdmin = useMemo(() => {
        return orgRole === ROLES.ADMIN;
    }, [orgRole]);

    return {
        can,
        canAny,
        canAll,
        hasRole,
        isAdmin,
        isLoaded,
        isSignedIn,
        orgRole,
    };
}
